import { useEffect, useState } from 'react';
import type { Idea, User } from '../api';
import * as api from '../api';
import { ManualIssue } from './GithubManual';

// "Open issue" in the idea panel (P5-10): turn the idea into a GitHub issue in
// one of the user's connected repos. Shown only on Pro with GitHub connected.

let statusOnce: Promise<api.GithubStatus | null> | null = null;
/** GitHub status, fetched once per page load (it changes only in Settings). */
function githubStatus(): Promise<api.GithubStatus | null> {
  statusOnce ??= api.getGithubStatus().catch(() => null);
  return statusOnce;
}

function starterBody(idea: Idea): string {
  const notes = idea.body?.trim() || idea.title;
  return `## Problem\n\n${notes}\n\n## Acceptance criteria\n\n- [ ] `;
}

export function OpenIssueSection({ idea, user }: { idea: Idea; user: User }) {
  const [enabled, setEnabled] = useState(false);
  const [open, setOpen] = useState(false);
  const [repos, setRepos] = useState<string[] | null>(null);
  const [repo, setRepo] = useState('');
  const [title, setTitle] = useState(idea.title);
  const [body, setBody] = useState(() => starterBody(idea));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [opened, setOpened] = useState<api.OpenedIssue | null>(null);

  useEffect(() => {
    if (user.plan !== 'pro') return;
    let live = true;
    githubStatus().then((s) => {
      if (live) setEnabled(Boolean(s?.available && s.installations.some((i) => !i.suspended)));
    });
    return () => { live = false; };
  }, [user.plan]);

  // A different idea in the same panel starts over.
  useEffect(() => {
    setOpen(false);
    setOpened(null);
    setError(null);
    setTitle(idea.title);
    setBody(starterBody(idea));
  }, [idea.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!enabled) return null;

  const start = () => {
    setOpen(true);
    setError(null);
    if (repos === null) {
      api.listGithubRepos()
        .then((r) => {
          const names = r.repos.map((x) => x.full_name);
          setRepos(names);
          setRepo((cur) => cur || names[0] || '');
        })
        .catch((err) => setError((err as Error).message));
    }
  };

  const submit = async () => {
    if (!repo) return;
    setBusy(true);
    setError(null);
    try {
      setOpened(await api.openIdeaIssue(idea.id, { repo, title: title.trim() || undefined, body }));
      setOpen(false);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="detail-section">
      <div className="detail-label">GITHUB</div>
      {opened?.manual && <ManualIssue draft={opened.manual} />}
      {opened?.issue && (
        <p className="issue-opened" role="status">
          {opened.existing ? 'Already open: ' : 'Opened '}
          <a href={opened.issue.url} target="_blank" rel="noopener noreferrer">{opened.issue.repo}#{opened.issue.number}</a>
        </p>
      )}
      {!open ? (
        <button className="btn btn-secondary btn-sm" onClick={start}>Open issue</button>
      ) : (
        <form className="link-search-popover issue-form" onSubmit={(e) => { e.preventDefault(); void submit(); }}>
          <label className="issue-field">
            <span>Repository</span>
            <select className="link-type-select" value={repo} onChange={(e) => setRepo(e.target.value)} disabled={!repos || repos.length === 0}>
              {repos === null && <option value="">Loading…</option>}
              {repos?.length === 0 && <option value="">No repositories connected</option>}
              {repos?.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </label>
          <label className="issue-field">
            <span>Title</span>
            <input className="link-search-input" value={title} maxLength={256} onChange={(e) => setTitle(e.target.value)} />
          </label>
          <label className="issue-field">
            <span>Body (Markdown, a link back to this idea is added)</span>
            <textarea className="link-search-input issue-body" value={body} rows={8} onChange={(e) => setBody(e.target.value)} />
          </label>
          {error && <p className="review-error" role="alert">{error}</p>}
          <div className="detail-actions-inline">
            <button type="submit" className="btn btn-primary btn-sm" disabled={busy || !repo}>{busy ? 'Opening…' : 'Open issue'}</button>
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => setOpen(false)} disabled={busy}>Cancel</button>
          </div>
        </form>
      )}
    </div>
  );
}
